import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { IntervalList, type IntervalRow } from '@/pages/anatel/conselho/IntervalList';
import { EvidenceCard, metricRows } from '@/pages/anatel/conselho/shared';
import { RunFooter } from '@/pages/anatel/conselho/RunFooter';
import { ValidationCard } from '@/pages/anatel/conselho/ValidationCard';
import { MinutesTable } from '@/pages/cade/conselho/MinutesTable';
import { cadeScopeLabel } from '@/pages/cade/conselho/scope';
import { conselhoCasesHref } from '@/pages/cade/ConselhoCasesPage';
import { VistaDocketSections } from '@/pages/cade/conselho/VistaDocketPanels';
import { fetchCadeConselho } from '@/services/cade';
import type { ConselhoPair, ConselhoPosition, ConselhoRosterMember, ConselhoSeiMinutes, ConselhoSgDocument } from '@/services/types';

/**
 * CADE Tribunal track: model-derived patterns on judged items.
 *
 * `/cade/votes` stays the named-minutes record. This page reads `tr_*` runs
 * fitted on atas, never on pauta-only docket rows, and never pools Law 8.884
 * with Law 12.529. Language: case-type residual, named-minutes record,
 * estimated position. Never logit, never a follow rate (the minutes cannot
 * support one; see MinutesTable), never a verdict about a person.
 */
export function CadeConselhoPage() {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'conselho'],
    queryFn: fetchCadeConselho,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={3} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const signed = new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 });
  const residual = (v: number) => signed.format(v);
  const pp = (v: number) => `${signed.format(v * 100)} pp`;
  const personLink = (row: IntervalRow) => (row.slug ? `/cade/r/${row.slug}` : null);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:conselho.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:conselho.subtitle')}</p>
        <Link
          to="/cade/conselho/methodology"
          className="inline-block text-xs text-primary underline-offset-4 hover:underline"
          data-testid="methodology-link"
        >
          {t('common:conselhoMethodology.link')}
        </Link>
      </header>

      <ExplainerCard i18nKey="cade:conselho.explainer" />
      <FieldGuide i18nKey="cade:conselho.guide" level={2} />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/cade/export/conselho" />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <EvidenceCard evidence={data.evidence} ns="cade" />

      <section className="rounded-lg border bg-card p-4" data-panel="roster">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.roster.title')}</h2>
        <Answer answer={data.roster} help={t('cade:conselho.roster.help')}>
          {(members) => <RosterTable members={members} />}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-panel="severity">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.severity.title')}</h2>
        <Answer answer={data.severity} help={t('cade:conselho.severity.help')}>
          {(rows) => (
            <IntervalList
              rows={metricRows(rows)}
              axisLabel={t('cade:conselho.severity.axis')}
              format={residual}
              rawFormat={(v) => percent.format(v)}
              rawLabel={t('cade:conselho.severity.raw')}
              linkTo={personLink}
            />
          )}
        </Answer>
        <ValidationCard validation={data.validation} ns="cade" />
        <RunFooter run={data.severity_run} ns="cade" />
      </section>

      <section className="rounded-lg border bg-card p-4" data-panel="minutes">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.minutes.title')}</h2>
        {data.minutes_record ? (
          <Answer answer={data.minutes_record} help={t('cade:conselho.minutes.help')}>
            {(rows) => <MinutesTable rows={rows} />}
          </Answer>
        ) : null}
        <div className="mt-4" data-panel="follow">
          <h3 className="mb-2 text-xs font-semibold text-foreground">{t('cade:conselho.minutes.followTitle')}</h3>
          <Answer answer={data.follow} help={t('cade:conselho.minutes.followHelp')}>
            {(rows) => (
              <IntervalList
                rows={metricRows(rows).map((r, i) => ({ ...r, name: rows[i].name }))}
                axisLabel={t('cade:conselho.minutes.followAxis')}
                format={(v) => percent.format(v)}
                zeroLine={false}
                linkTo={personLink}
              />
            )}
          </Answer>
        </div>
      </section>

      <VistaDocketSections vista={data.vista} docket={data.docket} relatorOutvoted={data.relator_outvoted}
        outvoted={data.outvoted} />

      <section className="rounded-lg border bg-card p-4" data-panel="pairs">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.pairs.title')}</h2>
        <Answer answer={data.pairs} help={t('cade:conselho.pairs.help')}>
          {(pairs) => <PairsTable pairs={pairs} />}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-panel="positions">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.positions.title')}</h2>
        <Answer answer={data.positions} help={t('cade:conselho.positions.help')}>
          {(rows) => (
            <IntervalList
              rows={positionRows(rows, t('cade:conselho.positions.contested'))}
              axisLabel={t('cade:conselho.positions.axis')}
              format={residual}
              linkTo={personLink}
            />
          )}
        </Answer>
        <RunFooter run={data.position_run} ns="cade" />
        <Link
          to="/cade/conselho/items"
          className="mt-3 inline-block text-xs text-primary underline-offset-4 hover:underline"
        >
          {t('cade:conselho.items.seeAll')}
        </Link>
      </section>

      {data.citation_families ? (
        <section className="rounded-lg border bg-card p-4" data-panel="citation-families">
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.citationFamilies.title')}</h2>
          <Answer answer={data.citation_families} help={t('cade:conselho.citationFamilies.help')}>
            {(rows) => (
              <IntervalList
                rows={metricRows(rows).map((row, i) => ({
                  ...row,
                  name: rows[i].scope === 'overall' ? rows[i].name : `${rows[i].name} — ${cadeScopeLabel(rows[i].scope, t)}`,
                }))}
                axisLabel={t('cade:conselho.citationFamilies.axis')}
                format={pp}
                rawFormat={(v) => percent.format(v)}
                rawLabel={t('cade:conselho.severity.raw')}
                linkTo={personLink}
              />
            )}
          </Answer>
          {data.citation_family_reach && data.citation_family_reach.labels > data.citation_family_reach.decisions ? (
            <p className="mt-2 text-[11px] text-muted-foreground" data-testid="citation-multilabel">
              {t('cade:conselho.citationFamilies.multiLabel', {
                labels: data.citation_family_reach.labels,
                decisions: data.citation_family_reach.decisions,
              })}
            </p>
          ) : null}
          <RunFooter run={data.citation_family_run ?? null} ns="cade" />
        </section>
      ) : null}

      {data.sg_document ? (
        <section className="rounded-lg border bg-card p-4" data-panel="sg-document">
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.sgDocument.title')}</h2>
          <Answer answer={data.sg_document} help={t('cade:conselho.sgDocument.help')} scope="record">
            {(doc) => <SgDocumentCard doc={doc} />}
          </Answer>
        </section>
      ) : null}

      {data.sei_minutes ? (
        <section className="rounded-lg border bg-card p-4" data-panel="sei-minutes">
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.seiMinutes.title')}</h2>
          <Answer answer={data.sei_minutes} help={t('cade:conselho.seiMinutes.help')} scope="record">
            {(doc) => <SeiMinutesCard doc={doc} />}
          </Answer>
        </section>
      ) : null}
    </div>
  );
}

function positionRows(rows: ConselhoPosition[], nLabel: string): IntervalRow[] {
  return rows.map((r) => ({
    key: `${r.name}|${r.period}`,
    name: r.period === 'all' ? r.name : `${r.name} — ${r.period}`,
    slug: r.slug,
    // n_items is the contested items THIS member sat; n_contested is the
    // run's total, the same on every row (ETL 42 majority_side_v1).
    n: r.n_items,
    nLabel,
    effect: r.position,
    lower: r.lower,
    upper: r.upper,
    suppressed: r.suppressed,
  }));
}

function SgDocumentCard({ doc }: { doc: ConselhoSgDocument }) {
  const { t } = useTranslation(['cade']);
  return (
    <div className="space-y-3 text-xs" data-testid="sg-document-card">
      <p className="font-medium text-foreground">{doc.title}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-muted-foreground">
        <dt>{t('cade:conselho.sgDocument.nup')}</dt>
        <dd className="text-foreground">{doc.nup}</dd>
        <dt>{t('cade:conselho.sgDocument.unit')}</dt>
        <dd className="text-foreground">{doc.unit ?? '—'}</dd>
        <dt>{t('cade:conselho.sgDocument.author')}</dt>
        <dd className="text-foreground">{doc.author_layer}</dd>
        <dt>{t('cade:conselho.sgDocument.kappa')}</dt>
        <dd className="text-foreground">{t('cade:conselho.sgDocument.kappaNa')}</dd>
      </dl>
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            <th className="p-2">{t('cade:conselho.sgDocument.dimension')}</th>
            <th className="p-2">{t('cade:conselho.sgDocument.value')}</th>
            <th className="p-2">{t('cade:conselho.sgDocument.quote')}</th>
          </tr>
        </thead>
        <tbody>
          {doc.codings.map((c) => (
            <tr key={c.dimension} className="border-t">
              <td className="p-2">{c.dimension}</td>
              <td className="p-2">{c.value}</td>
              <td className="p-2 text-muted-foreground">
                {c.evidence_verified && c.evidence_span ? c.evidence_span : t('cade:conselho.sgDocument.unverified')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SeiMinutesCard({ doc }: { doc: ConselhoSeiMinutes }) {
  const { t } = useTranslation(['cade']);
  const issues = Array.from(new Set(doc.issues.map((v) => v.issue_slug)));
  return (
    <div className="space-y-3 text-xs" data-testid="sei-minutes-card">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-muted-foreground">
        <dt>{t('cade:conselho.seiMinutes.nup')}</dt>
        <dd className="text-foreground">{doc.nup}</dd>
        <dt>{t('cade:conselho.seiMinutes.documents')}</dt>
        <dd className="text-foreground">
          {t('cade:conselho.seiMinutes.documentCounts', {
            documents: doc.n_documents,
            certidoes: doc.n_certidoes,
            votos: doc.n_votos,
          })}
        </dd>
      </dl>
      {doc.collapse_refused ? (
        <p className="text-muted-foreground">{t('cade:conselho.seiMinutes.collapseRefused')}</p>
      ) : null}
      {issues.map((slug) => {
        const rows = doc.issues.filter((v) => v.issue_slug === slug);
        const label = rows[0]?.issue_label ?? slug;
        return (
          <div key={slug}>
            <p className="font-medium text-foreground">{label}</p>
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-muted text-left">
                <tr>
                  <th className="p-2">{t('cade:conselho.seiMinutes.voter')}</th>
                  <th className="p-2">{t('cade:conselho.seiMinutes.position')}</th>
                  <th className="p-2">{t('cade:conselho.seiMinutes.quote')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={`${slug}-${v.voter_canonical}`} className="border-t">
                    <td className="p-2">{v.voter_canonical}</td>
                    <td className="p-2">{v.position}</td>
                    <td className="p-2 text-muted-foreground">
                      {v.evidence_verified && v.evidence_span
                        ? v.evidence_span
                        : t('cade:conselho.seiMinutes.unverified')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function RosterTable({ members }: { members: ConselhoRosterMember[] }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            <th className="p-2">{t('cade:conselho.roster.name')}</th>
            <th className="p-2">{t('cade:conselho.roster.roles')}</th>
            <th className="p-2">{t('cade:conselho.roster.span')}</th>
            <th className="p-2 text-right">{t('cade:conselho.roster.relatorias')}</th>
            <th className="p-2 text-right">{t('cade:conselho.roster.pautas')}</th>
            <th className="p-2 text-right">{t('cade:conselho.roster.presences')}</th>
            <th className="p-2 text-right">{t('cade:conselho.roster.votos')}</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.slug} className="border-t">
              <td className="p-2">
                <Link to={`/cade/r/${m.slug}`} className="text-primary underline-offset-4 hover:underline">
                  {m.name}
                </Link>
              </td>
              <td className="p-2 text-muted-foreground">{m.roles.join(', ')}</td>
              <td className="p-2 tabular-nums text-muted-foreground">
                {m.first_seen && m.last_seen ? `${m.first_seen} — ${m.last_seen}` : '—'}
              </td>
              <td className="p-2 text-right tabular-nums">{number.format(m.n_relatorias)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(m.n_analises)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(m.n_presences)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(m.n_votos)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PairsTable({ pairs }: { pairs: ConselhoPair[] }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 });
  // Listed first, then flagged -- never filtered out, so a bench whose pairs
  // are all below a floor still shows its counts.
  const listed = pairs.filter((p) => !p.suppressed);
  const flagged = pairs.filter((p) => p.suppressed);
  const ordered = [...listed, ...flagged];
  const rate = (share: number | null | undefined, lower: number | null | undefined, upper: number | null | undefined) =>
    share == null ? '—' : lower == null || upper == null ? percent.format(share) : `${percent.format(share)} [${percent.format(lower)}, ${percent.format(upper)}]`;
  return (
    <div className="space-y-2">
      <div className="max-h-96 overflow-auto">
        <table className="w-full text-xs" data-testid="pairs-table">
          <thead className="sticky top-0 z-10 bg-muted text-left">
            <tr>
              <th className="p-2">A</th>
              <th className="p-2">B</th>
              <th className="p-2 text-right">{t('cade:conselho.pairs.coSat')}</th>
              <th className="p-2 text-right">{t('cade:conselho.pairs.contested')}</th>
              <th className="p-2 text-right">{t('cade:conselho.pairs.agreeContested')}</th>
              <th className="p-2 text-right">{t('cade:conselho.pairs.agreeAll')}</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((p) => (
              <tr
                key={`${p.slug_a}|${p.slug_b}`}
                className={`border-t ${p.suppressed ? 'text-muted-foreground' : ''}`}
                data-suppressed={p.suppressed ? 'true' : 'false'}
              >
                <td className="p-2">
                  {p.name_a}
                  {p.suppressed ? (
                    <span className="ml-1 text-muted-foreground">{t('cade:conselho.pairs.belowFloor')}</span>
                  ) : null}
                </td>
                <td className="p-2">{p.name_b}</td>
                <td className="p-2 text-right tabular-nums">
                  <Link
                    to={conselhoCasesHref('pair', p.name_a, p.name_b)}
                    className="underline-offset-4 hover:underline"
                    data-testid="pair-items-link"
                  >
                    {number.format(p.n_co_sat)}
                  </Link>
                </td>
                <td className="p-2 text-right tabular-nums">{number.format(p.n_contested_co_sat)}</td>
                <td className="p-2 text-right tabular-nums" data-testid="pair-agree-contested">
                  {rate(p.agreement_rate_contested, p.lower_contested, p.upper_contested)}
                </td>
                <td className="p-2 text-right tabular-nums text-muted-foreground" data-testid="pair-agree-all">
                  {rate(p.agreement_rate, p.lower, p.upper)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {flagged.length > 0 ? (
        <p className="text-[11px] text-muted-foreground" data-testid="pairs-flagged">
          {number.format(flagged.length)} {t('cade:conselho.pairs.belowFloor')}
        </p>
      ) : null}
    </div>
  );
}
